import { useState } from 'react';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import { patchRoutine, type WebRoutine } from '../../../lib/routines';
import { routineIsOn, routineStatusBadge, routineToggleEnabled } from '../../../lib/routine-status';
import { ROUTINES_PAGE } from '../../../lib/routines-bi';
import { AgentDot } from '../../AgentDot';
import { Badge, Card, Switch } from '../ui';
import { useRoutineCopy } from './use-routine-copy';
import { SkillConnectorLine } from '../agents/SkillConnectorLine';
import styles from './routines.legacy.module.css';

// One card per routine, readable WITHOUT opening it — the whole point of the redesign. A status row
// leads (the badge at one edge, the schedule and the pause switch at the other), then the title, what it
// does, and under a hairline the two facts a person asks next: which agent last worked it, and how it
// has been going.
//
// The card itself is the affordance (a DS Card button opening the detail modal), so the badge, the
// schedule and the switch are laid OVER its top edge rather than placed inside it: the card renders as a
// button element, and a button inside a button is invalid HTML that browsers resolve unpredictably —
// the same construction the task card uses.
//
// **There is no row menu.** Edit, Chat and Delete all live inside the routine's own detail, and a
// three-dot on every row was a second place to keep them in step with no reader asking for one.
export function RoutineCard({
  routine,
  spaceName,
  flat,
  onOpen,
}: {
  routine: WebRoutine;
  /** Omitted when the Space axis is fixed — repeating the Space the reader just filtered to says nothing. */
  spaceName?: string;
  /** This card is a ROW inside its connector's card. The surface needs no change to say so — the sunken
   *  skin is white with a transparent border, so a nested one reads as part of the card it sits in and
   *  still takes the row hover. What it drops is the connector credit line, which the card's own header
   *  has already said. */
  flat?: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { titleOf, scheduleText, nextRunText, activityText, runCountText, statusLabel } = useRoutineCopy();
  const [error, setError] = useState<string | null>(null);

  const title = titleOf(routine);
  const badge = routineStatusBadge(routine);
  const entity = { entity: { id: routine.id, type: ENTITY_TYPE.pulse } };

  const description = routine.description?.trim();
  const schedule = [scheduleText(routine), nextRunText(routine)].filter(Boolean).join(' · ');
  // Each of these is dropped when it has nothing true to say — an unknown run history renders NOTHING
  // rather than an absence (see `routineActivity`), and a fixed Space axis drops the Space.
  const meta = [spaceName, activityText(routine), runCountText(routine)].filter(Boolean).join(' · ');

  const onToggle = async (next: boolean) => {
    logPageClickBi(
      { ...ROUTINES_PAGE, button_name: 'toggle_routine', state: next ? 'on' : 'off' },
      entity,
    );
    setError(null);
    const result = await patchRoutine(routine, { paused: !next });
    if (!result.ok) setError(t('routines.edit.error.generic'));
  };

  const open = () => {
    logPageClickBi({ ...ROUTINES_PAGE, button_name: 'open_routine' }, { ...entity, componentName: 'open-routine' });
    onOpen();
  };

  const content = (
    <>
      <div className={styles.cardHeader}>
          <span className={styles.title}>{title}</span>
          {description && <span className={styles.description}>{description}</span>}
          <span className={styles.scheduleInline}>{schedule}</span>
        </div>
        {(routine.agentName || meta) && (
          <div className={styles.cardMeta}>
            {routine.agentName && (
              <span className={styles.agentTag}>
                {routine.agentId ? <AgentDot id={routine.agentId} size={15} /> : null}
                <span className={styles.agentName}>{routine.agentName}</span>
              </span>
            )}
            {meta ? <span className={styles.metaText}>{meta}</span> : null}
          </div>
        )}
        {/* Which connected app this routine came with — nothing for a routine the user or the agent made,
            and nothing inside that connector's own card, where the header has just said it. */}
        {!flat && <SkillConnectorLine slug={routine.sourceConnectorSlug} />}
        {error && <span className={styles.error}>{error}</span>}
    </>
  );

  return (
    <div className={styles.cardRow}>
      <Card component="button" interactive className={styles.card} onClick={open}>
        {content}
      </Card>
      <div className={styles.cardControls}>
        <Badge variant={badge.variant} label={statusLabel(routine, badge)} />
        <span className={styles.controlsEnd}>
          {/* Desktop puts the schedule beside the switch (Figma 18352-340460); the phone keeps it in
              the card's own column, under the description. One of the two is always `display: none`,
              which also keeps it out of the accessibility tree — so it is never announced twice. */}
          <span className={styles.scheduleCorner}>{schedule}</span>
          {/* A credit-paused routine cannot be resumed from here: writing `paused: false` would be undone
              by the next credit check, so the control would lie. The badge already names the way out. */}
          {routine.ended !== true && (
            <Switch
              checked={routineIsOn(routine)}
              onChange={onToggle}
              disabled={!routineToggleEnabled(routine)}
              aria-label={t('routines.toggle.label', { name: title })}
            />
          )}
        </span>
      </div>
    </div>
  );
}
